import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('ledgerpro_token') || localStorage.getItem('token');
}

/**
 * Reusable Customer/Vendor select with inline "Add New" capability.
 * Props:
 *   value - selected ID
 *   onChange - callback with { id, name, ...customerData }
 *   type - 'customer' | 'vendor'
 *   placeholder - input placeholder
 */
export default function CustomerSelect({ value, onChange, type = 'customer', placeholder }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', address: '', tax_id: '',
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const isVendor = type === 'vendor';
  const endpoint = isVendor ? '/vendors' : '/customers';
  const label = isVendor ? 'Vendor' : 'Customer';

  // Load items
  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : (data.customers || data.vendors || []));
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadItems(); }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
        setShowAddForm(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedItem = items.find(i => String(i.id) === String(value));

  const filtered = items.filter(i =>
    (i.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.company_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (item) => {
    onChange(item);
    setSearch('');
    setShowDropdown(false);
  };

  const handleAddNew = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(`${label} name is required`);
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          company_name: form.company.trim() || undefined,
          address: form.address.trim() || undefined,
          tax_id: form.tax_id.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create');
      }
      const newItem = await res.json();
      toast.success(`${label} "${form.name}" created!`);
      setItems(prev => [...prev, newItem]);
      onChange(newItem);
      setForm({ name: '', email: '', phone: '', company: '', address: '', tax_id: '' });
      setShowAddForm(false);
      setShowDropdown(false);
      setSearch('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div ref={dropdownRef} style={{ position: 'relative', flex: 1, minWidth: 200 }}>
      {/* Input */}
      <div
        onClick={() => { setShowDropdown(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        style={{
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 13,
          color: selectedItem ? '#0f172a' : '#94a3b8',
          minHeight: 38,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedItem ? selectedItem.name : (placeholder || `Select ${label}...`)}
        </span>
        <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginLeft: 8 }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          zIndex: 1000,
          maxHeight: 320,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}s...`}
              style={{
                width: '100%',
                padding: '7px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Items list */}
          <div style={{ overflowY: 'auto', maxHeight: 200 }}>
            {loading ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No {label.toLowerCase()}s found
              </div>
            ) : (
              filtered.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: String(item.id) === String(value) ? '#eff6ff' : 'transparent',
                    borderBottom: '1px solid #f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = String(item.id) === String(value) ? '#eff6ff' : 'transparent'}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.name}</div>
                  {(item.email || item.company_name) && (
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {item.company_name && <span>{item.company_name}</span>}
                      {item.company_name && item.email && <span> · </span>}
                      {item.email && <span>{item.email}</span>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Add New Button / Form */}
          <div style={{ borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'inherit',
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
                Add New {label}
              </button>
            ) : (
              <form onSubmit={handleAddNew} style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>New {label}</div>
                <input
                  value={form.name}
                  onChange={set('name')}
                  placeholder={`${label} name *`}
                  autoFocus
                  style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
                />
                <input
                  value={form.email}
                  onChange={set('email')}
                  placeholder="Email"
                  type="email"
                  style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="Phone"
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
                  />
                  <input
                    value={form.company}
                    onChange={set('company')}
                    placeholder="Company"
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
                  />
                </div>
                <input
                  value={form.address}
                  onChange={set('address')}
                  placeholder="Address"
                  style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setForm({ name: '', email: '', phone: '', company: '', address: '', tax_id: '' }); }}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#64748b', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !form.name.trim()}
                    style={{ flex: 1, padding: '7px 10px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: '#2563eb', cursor: 'pointer', opacity: (saving || !form.name.trim()) ? 0.5 : 1, fontFamily: 'inherit' }}
                  >
                    {saving ? 'Saving...' : `Create ${label}`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
